import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Search, Laptop, Smartphone, Server, Shield, Building, Users, MapPin, CheckCircle, AlertTriangle, XCircle, Home, Wifi, Lock, Eye, Key, Briefcase, HardDrive, Monitor, Tablet, User } from "lucide-react";
import * as LucideIcons from "lucide-react";

interface IconSelectorProps {
  value?: string;
  onChange: (iconName: string) => void;
  placeholder?: string;
}

// Emoji and icon options for tags
const iconCategories = {
  "Devices": {
    laptop: Laptop,
    smartphone: Smartphone,
    server: Server,
    monitor: Monitor,
    tablet: Tablet,
    "hard-drive": HardDrive,
  },
  "People & Teams": {
    users: Users,
    user: User,
    briefcase: Briefcase,
    building: Building,
  },
  "Status & Alerts": {
    "check-circle": CheckCircle,
    "alert-triangle": AlertTriangle,
    "x-circle": XCircle,
  },
  "Security": {
    shield: Shield,
    lock: Lock,
    eye: Eye,
    key: Key,
  },
  "Locations": {
    "map-pin": MapPin,
    home: Home,
    wifi: Wifi,
    building: Building,
  },
};

export function IconSelector({ value, onChange, placeholder = "Select an icon" }: IconSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Get all available icons from categories
  const allIcons = Object.values(iconCategories).reduce((acc, category) => {
    return { ...acc, ...category };
  }, {});

  // Filter icons based on search
  const filteredCategories = Object.entries(iconCategories).reduce((acc, [categoryName, icons]) => {
    const filteredIcons = Object.entries(icons).filter(([iconName]) =>
      iconName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    if (filteredIcons.length > 0) {
      acc[categoryName] = Object.fromEntries(filteredIcons);
    }
    return acc;
  }, {} as typeof iconCategories);

  const handleIconSelect = (iconName: string) => {
    onChange(iconName);
    setOpen(false);
  };

  const selectedIcon = value ? allIcons[value as keyof typeof allIcons] : null;
  const SelectedIconComponent = selectedIcon || Eye;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 justify-center p-0"
        >
          <SelectedIconComponent className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4">
        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search icons..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9"
            />
          </div>

          {/* Quick Access - Most Common Icons */}
          {!searchQuery && (
            <div>
              <h4 className="text-xs font-semibold text-foreground mb-3 uppercase tracking-wider">
                Popular
              </h4>
              <div className="grid grid-cols-6 gap-2 mb-4">
                {["laptop", "users", "shield", "building", "check-circle", "map-pin", "lock", "briefcase"].map((iconName) => {
                  const IconComponent = allIcons[iconName as keyof typeof allIcons] as React.ComponentType<{ className?: string }>;
                  if (!IconComponent) return null;
                  return (
                    <Button
                      key={iconName}
                      variant={value === iconName ? "default" : "ghost"}
                      size="sm"
                      className="h-10 w-10 p-0 hover:bg-accent transition-colors"
                      onClick={() => handleIconSelect(iconName)}
                    >
                      <IconComponent className="h-4 w-4" />
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="space-y-4 max-h-60 overflow-y-auto">
            {Object.entries(filteredCategories).map(([categoryName, icons]) => (
              <div key={categoryName}>
                <h4 className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wider border-b border-border pb-1">
                  {categoryName}
                </h4>
                <div className="grid grid-cols-6 gap-2">
                  {Object.entries(icons).map(([iconName, IconComponent]) => (
                    <Button
                      key={iconName}
                      variant={value === iconName ? "default" : "ghost"}
                      size="sm"
                      className="h-10 w-10 p-0 hover:bg-accent transition-colors"
                      onClick={() => handleIconSelect(iconName)}
                      title={iconName.replace('-', ' ')}
                    >
                      <IconComponent className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {Object.keys(filteredCategories).length === 0 && searchQuery && (
            <div className="text-center py-6 text-muted-foreground text-sm">
              <div className="mb-2">No icons found</div>
              <div className="text-xs">Try a different search term</div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}